import BottomSheet from "../components/BottomSheet.js";
import MovieList from "../components/MovieList.js";
import SettingsForm from "../components/SettingsForm.js";
import { loadLunrSearch, getJSON } from "../helpers.js";
import { getCurrentUser } from "./Auth.js";

const ProfileDialog = (element, options = {}) => {
  const el = element;
  const opts = {
    ...options,
  };

  let settingsForm;
  let watchlistMovieList;

  const handleProfilFormSubmit = (event, submitButton) => {
    event.preventDefault();
    switch (submitButton.id) {
      case "logout-button":
        if (opts.onLogout) opts.onLogout();
    }
  };

  /**
   * Render watchlist movies in the UI
   * @param {Array} movies - Array of movie objects to display
   */
  const renderWatchlist = (movies) => {
    if (movies.length > 0) {
      watchlistMovieList.setMessage(movies.length + " filmet vettél fel a kedvenc listádra.");
      movies.forEach((movie) => {
        watchlistMovieList.addMovie({
          id: parseInt(movie.id),
          slug: movie.slug,
          delayed: movie.delayed,
          soon: movie.soon,
          poster: movie.poster_thumb || movie.poster,
          distributor: movie.distributor,
          release_date: movie.hu_release_nice || movie.release_date,
          title: movie.hu_title || movie.title,
          age_limit: movie.age_limit
            ? typeof movie.age_limit === "string"
              ? movie.age_limit
              : movie.age_limit[0]
            : null,
          original_title: movie.original_title,
          year: parseInt(movie.year),
          subtitle: movie.subtitle,
          top_country: movie.top_country,
          genres: movie.genres,
          runtime: movie.runtime || 90,
        });
      });
    } else {
      watchlistMovieList.setMessage(
        "Még nincs film a kedvenc listádon. :(<br><br>A Tetszik gombbal vehetsz fel ide filmeket."
      );
    }
  };

  /**
   * Load watchlist using legacy API endpoint
   * @param {string} apiToken - API authentication token
   */
  // eslint-disable-next-line no-unused-vars
  const loadWatchlist = async (apiToken) => {
    try {
      watchlistMovieList.reset();
      watchlistMovieList.setMessage('<i class="fas fa-circle-notch fa-spin"></i> kedvencek betöltése...');
      const result = await getJSON("/api/watchlist/?token=" + encodeURIComponent(apiToken));
      if (result.status !== "OK") {
        throw new Error(result);
      }
      const data = result.data;
      renderWatchlist(data.movies);
    } catch (error) {
      if (process.env.NODE_ENV === "development") console.error("Failed to load watchlist:", error);
      watchlistMovieList.setMessage("Hiba történt a kedvencek betöltésekor.");
    }
  };

  /**
   * Load watchlist using Lunr index (optimized, no API call)
   * @param {Object} user - User object containing watchlist property
   */
  const loadWatchlistUsingLunrIndex = async (user) => {
    try {
      watchlistMovieList.reset();
      watchlistMovieList.setMessage('<i class="fas fa-circle-notch fa-spin"></i> kedvencek betöltése...');

      if (!user || !user.watchlist || user.watchlist.length === 0) {
        renderWatchlist([]);
        return;
      }

      // Load Lunr search index
      const lunrSearch = await loadLunrSearch();

      // Enrich movie IDs with data from Lunr index (O(1) lookup via idToRefMap)
      const movies = [];
      const missingMovies = [];

      user.watchlist.forEach((movieId) => {
        const doc = lunrSearch.getDocumentById(movieId);

        if (doc) {
          movies.push({
            id: movieId,
            slug: doc.slug,
            delayed: doc.delayed,
            soon: doc.soon,
            poster: doc.generated_poster_thumb,
            distributor: doc.distributor,
            release_date: doc.hu_release_nice,
            title: doc.hu_title,
            age_limit: doc.age_limit,
            original_title: doc.original_title || doc.title,
            year: doc.year,
            subtitle: doc.subtitle,
            top_country: doc.top_country,
            genres: doc.genres,
            runtime: doc.runtime || 90,
            hu_release_date: doc.hu_release_date, // For sorting
          });
        } else {
          missingMovies.push(movieId);
        }
      });

      // Sort by release date descending (newest first)
      movies.sort((a, b) => {
        const dateA = new Date(a.hu_release_date || 0);
        const dateB = new Date(b.hu_release_date || 0);
        return dateB - dateA;
      });

      // Render movies
      renderWatchlist(movies);

      // Log warning if some movies are missing from index
      if (missingMovies.length > 0) {
        if (process.env.NODE_ENV === "development")
          console.warn(`${missingMovies.length} movie(s) not found in Lunr index:`, missingMovies);
      }
    } catch (error) {
      if (process.env.NODE_ENV === "development") console.error("Failed to load watchlist using Lunr index:", error);
      watchlistMovieList.setMessage("Hiba történt a kedvencek betöltésekor.");
    }
  };

  const component = BottomSheet(el, {
    componentName: "ProfileDialog",
    closeOnBackdropClick: true,
    scrollBody: true,
    onOpen: async (user, apiToken, calendarToken) => {
      settingsForm = SettingsForm(el.querySelector("#profil-settings-form"), {
        onSubmit: (event, submitButton) => handleProfilFormSubmit(event, submitButton),
      }).mount();
      watchlistMovieList = MovieList(el.querySelector(".watchlist"), el.querySelector(".watchlist-message"), {
        inverse: true,
      }).mount();
      settingsForm.enable();

      // Get fresh user data (automatically refreshes if stale)
      const freshUser = await getCurrentUser();
      if (!freshUser) {
        watchlistMovieList.setMessage("Hiba: nem sikerült betölteni a felhasználói adatokat.");
        return;
      }

      settingsForm.form.querySelector(".profil-greeting").innerHTML = "Szia, " + freshUser.name + "!";
      settingsForm.form.querySelector("#calendar-link").href =
        "webcal://" + document.location.hostname + "/calendar/" + calendarToken + ".ics";

      // Switch between implementations by commenting/uncommenting:
      // await loadWatchlist(apiToken); // Legacy API-based loading
      await loadWatchlistUsingLunrIndex(freshUser); // Optimized Lunr-based loading (no API call)
    },
    onClose: async () => {
      if (settingsForm) {
        settingsForm.unmount();
      }
      if (watchlistMovieList) {
        watchlistMovieList.unmount();
      }
    },
  });

  return component;
};

export default ProfileDialog;
